import type { GeometryObject } from 'geojson';
import createUrlBuilder from 'utilities/url.builder.utils';
import { getRequest, postRequest } from 'utilities/service.utils';
import AppService from './app.service.context';

// ---------------------------------------------------------------------------
// Register member lookup request bodies
// ---------------------------------------------------------------------------

export interface RegisterMemberLookupMapSketchBody {
  inputSource: 'MAP_SKETCH';
  shapes: GeometryObject[];
}

export interface RegisterMemberLookupFileUploadBody {
  inputSource: 'FILE_UPLOAD';
  fileId: number;
}

export type RegisterMemberLookupBody =
  | RegisterMemberLookupMapSketchBody
  | RegisterMemberLookupFileUploadBody;

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

export interface RegisterMemberLookupResponse {
  lookupId: number;
  jobId?: number; // only present for FILE_UPLOAD path
}

export interface LivePriceResponse {
  paymentType: string;
  currency: string;
  taxBehavior: string;
  unitAmount: number; // in cents, e.g. 5000 ($50.00)
  unitAmountDecimal: string; // e.g. '5000'
}

export interface CreateCheckoutResponse {
  clientSecret?: string;
  completed?: boolean;
  postPaymentId?: number;
  transactionReference?: string;
}

export interface CheckoutStatusResponse {
  status: 'open' | 'complete' | 'expired';
  paymentType: string | null;
  paymentIntentId?: string;
}

export interface MemberLookupStatusResponse {
  lookupId: number;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  systemJobId: number | null;
  shapeCount?: number;
  areaKm2?: number;
}

export interface JobStatusResponse {
  id: number;
  type: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  request: { [key: string]: any };
  response: { [key: string]: any };
  createdAt: string;
  updatedAt: string;
}

export interface DownloadResponse {
  url: string;
  memberCount: number;
  fileName: string;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  fileId: number;
}

// Raw shape of POST /system/uploads response
interface SystemUploadResponse {
  id: number;
  key: string;
  method: string;
  url: string;
}

interface DownloadApiResponse {
  downloadUrl?: string;
  url?: string;
  memberCount?: number;
  fileName?: string;
}

// ---------------------------------------------------------------------------
// Error normalisation
// ---------------------------------------------------------------------------

/**
 * postRequest/getRequest from service.utils never throw — on any failure they
 * catch internally and return the error as the resolved value.
 */
function assertNoError(response: any, context: string): void {
  if (!response) {
    throw Object.assign(new Error(`${context}: empty response`), {
      status: 500
    });
  }
  if (response instanceof Error) {
    throw Object.assign(response, { status: 0 });
  }
  if (response.error?.code) {
    const status =
      typeof response.error.code === 'number' ? response.error.code : 400;
    throw Object.assign(
      new Error(`${context}: ${response.error.message ?? 'API error'}`),
      { status }
    );
  }
  const status = response.status ?? response.statusCode;
  if (typeof status === 'number' && status >= 400) {
    throw Object.assign(new Error(`${context}: HTTP ${status}`), { status });
  }
  if (response.errors) {
    throw Object.assign(
      new Error(`${context}: ${response.errors[0]?.message ?? 'API error'}`),
      { status: 400 }
    );
  }
}

// ---------------------------------------------------------------------------
// File upload to S3
// ---------------------------------------------------------------------------

export const getUploadUrl = async (
  fileName: string
): Promise<UploadUrlResponse> => {
  const url = createUrlBuilder('/system/uploads').toString();
  const response: SystemUploadResponse = await postRequest(AppService, url, {
    name: fileName,
    entity: 'MemberLookupUpload'
  });
  assertNoError(response, 'getUploadUrl');
  return { uploadUrl: response.url, fileId: response.id };
};

export const uploadFileToS3 = async (
  presignedUrl: string,
  file: File
): Promise<Response> => {
  const body = await file.arrayBuffer();
  return await fetch(presignedUrl, {
    method: 'PUT',
    body,
    headers: {
      'Content-Type': file.type || 'application/octet-stream'
    }
  });
};

// ---------------------------------------------------------------------------
// Register member lookup session
// ---------------------------------------------------------------------------

export const registerMemberLookup = async (
  body: RegisterMemberLookupBody
): Promise<RegisterMemberLookupResponse> => {
  const url = createUrlBuilder(
    '/community/byda-member-lookup/register'
  ).toString();
  const response = await postRequest(AppService, url, body);
  assertNoError(response, 'registerMemberLookup');
  return response;
};

// ---------------------------------------------------------------------------
// Live price fetch
// ---------------------------------------------------------------------------

export const getLivePrice = async (): Promise<LivePriceResponse> => {
  const url = createUrlBuilder('/payment/prices/BYDA_MEMBER_LOOKUP').toString();
  const response = await getRequest(AppService, url);
  assertNoError(response, 'getLivePrice');
  return response;
};

// ---------------------------------------------------------------------------
// Stripe checkout session
// ---------------------------------------------------------------------------

export const createCheckoutSession = async (
  lookupId: number,
  userId: number,
  returnUrl: string
): Promise<CreateCheckoutResponse> => {
  const url = createUrlBuilder('/payment/checkout').toString();
  const response = await postRequest(AppService, url, {
    paymentType: 'BYDA_MEMBER_LOOKUP',
    returnUrl,
    metaData: {
      lookupId: String(lookupId),
      userId: String(userId)
    }
  });
  assertNoError(response, 'createCheckoutSession');
  return response;
};

export const getCheckoutStatus = async (
  sessionId: string
): Promise<CheckoutStatusResponse> => {
  const url = createUrlBuilder(
    `/payment/checkout/status/${sessionId}`
  ).toString();
  const response = await getRequest(AppService, url);
  assertNoError(response, 'getCheckoutStatus');
  return response;
};

// ---------------------------------------------------------------------------
// Member lookup status polling (post-payment)
// ---------------------------------------------------------------------------

export const getMemberLookupStatus = async (
  lookupId: number
): Promise<MemberLookupStatusResponse> => {
  const url = createUrlBuilder(
    `/community/byda-member-lookup/${lookupId}/status`
  ).toString();
  const response = await getRequest(AppService, url);
  assertNoError(response, 'getMemberLookupStatus');
  return response;
};

// ---------------------------------------------------------------------------
// Job status polling (pre-payment FILE_UPLOAD validation)
// ---------------------------------------------------------------------------

export const getJobStatus = async (
  jobId: number
): Promise<JobStatusResponse> => {
  const url = createUrlBuilder(`/system/jobs/${jobId}`).toString();
  const response = await getRequest(AppService, url);
  assertNoError(response, 'getJobStatus');
  return response;
};

// ---------------------------------------------------------------------------
// Download URL
// ---------------------------------------------------------------------------

export const getDownloadUrl = async (
  lookupId: number
): Promise<DownloadResponse> => {
  const url = createUrlBuilder(
    `/community/byda-member-lookup/${lookupId}/download`
  ).toString();
  const response: DownloadApiResponse = await getRequest(AppService, url);
  assertNoError(response, 'getDownloadUrl');
  const downloadUrl = response.downloadUrl ?? response.url;
  if (!downloadUrl) {
    throw Object.assign(new Error('getDownloadUrl: missing download URL'), {
      status: 500
    });
  }
  return {
    url: downloadUrl,
    memberCount: response.memberCount ?? 0,
    fileName: response.fileName ?? 'member-lookup.csv'
  };
};
